import _extends from '@babel/runtime/helpers/extends';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _merge from 'lodash/merge';
import _isNil from 'lodash/isNil';
import _cloneDeep from 'lodash/cloneDeep';
import React, { useRef, useState, useCallback, useEffect } from 'react';
import { ButtonLeftIcon, ButtonRightIcon } from '@manulife/mux-cds-icons';
import PropTypes from '../../../node_modules/prop-types/index.js';
import { SVG_ICON_SIZE_DEFAULT } from '../Icon/constants.js';
import { BREAKPOINTS } from '../../spacing/index.js';
import { safeRest, noop, rem } from '../../utils/functions.js';
import getDefaultTab from '../../utils/getDefaultTab.js';
import handle from '../../utils/handle.js';
import safeCustomStyle from '../../utils/safeCustomStyle.js';
import { TAB_VARIANT, TAB_ALIGNMENT, SCROLL_DIRECTION } from './constants.js';
import { TabListWrapper, TabList, TabItem, TabButton, ScrollButton } from './styledComponents.js';
import { fontWeightOffset, animate } from './utils.js';
import useTheme from '../../hooks/useTheme.js';
import useDerivedState from '../../hooks/useDerivedState.js';
import useMedia from '../../hooks/useMedia.js';
import componentWithName from '../../prop-types/componentWithName.js';

var _excluded = ["onChange", "tabItems", "selectedTab", "defaultSelectedTab", "controlledSelectedTab", "isSticky", "tabAlignment", "variant", "scrollButtonAriaLabel", "mobileTabAlignment", "customStyle", "children"];
function renderSelectedTabContent(tabItems, selectedTab, children, customStyle) {
  if (!tabItems || _isNil(selectedTab) || !children) {
    return null;
  }
  var selectedIndex = tabItems.findIndex(function (tab) {
    return tab.value === selectedTab;
  });
  var selectedChild = children && React.Children.toArray(children)[selectedIndex];
  var style = _cloneDeep(customStyle);

  // Allowing user to pass customStyle in the Tabs component (for all TabPanels)
  // or in the TabPanel component for individual styles
  _merge(style, selectedChild.props.customStyle);
  return /*#__PURE__*/React.cloneElement(selectedChild, {
    id: tabItems[selectedIndex].id,
    customStyle: {
      tabPanelStyle: style.tabPanelStyle
    },
    tabItems: tabItems
  });
}
var Tabs = function Tabs(_ref) {
  var onChange = _ref.onChange,
    tabItems = _ref.tabItems,
    legacySelectedTab = _ref.selectedTab,
    defaultSelectedTab = _ref.defaultSelectedTab,
    controlledSelectedTab = _ref.controlledSelectedTab,
    isSticky = _ref.isSticky,
    tabAlignment = _ref.tabAlignment,
    variant = _ref.variant,
    scrollButtonAriaLabel = _ref.scrollButtonAriaLabel,
    mobileTabAlignment = _ref.mobileTabAlignment,
    customStyle = _ref.customStyle,
    children = _ref.children,
    rest = _objectWithoutProperties(_ref, _excluded);
  var theme = useTheme();
  var safeProps = safeRest(rest);
  var _safeCustomStyle = safeCustomStyle(customStyle),
    tabsWrapperStyle = _safeCustomStyle.tabsWrapperStyle,
    tabItemStyle = _safeCustomStyle.tabItemStyle;
  var numTabs = tabItems.length;
  var _useDerivedState = useDerivedState(legacySelectedTab || defaultSelectedTab, controlledSelectedTab),
    _useDerivedState2 = _slicedToArray(_useDerivedState, 2),
    selectedTabState = _useDerivedState2[0],
    setSelectedTab = _useDerivedState2[1];
  // NOTE: Finding default tab here rather than within our state call, because we want to ensure that none of our
  // options result in selecting an invalid tab
  var selectedTab = getDefaultTab(tabItems, selectedTabState);
  var isTablet = useMedia(["(max-width: ".concat(BREAKPOINTS.MAX_1024, "px)")],
  // media queries
  [true],
  // values corresponding to media query
  false // default value
  );

  var tabListRef = useRef(null);
  var _useState = useState({
      left: false,
      right: false
    }),
    _useState2 = _slicedToArray(_useState, 2),
    displayScroll = _useState2[0],
    setDisplayScroll = _useState2[1];
  var isScrollable = isTablet && variant === TAB_VARIANT.GREY;
  var showScrollButtons = isScrollable && (displayScroll.left || displayScroll.right);
  var updateDisplayScrollState = useCallback(function () {
    var showLeftScroll = tabListRef.current.scrollLeft > 0;
    var showRightScroll = tabListRef.current.scrollLeft < tabListRef.current.scrollWidth - tabListRef.current.clientWidth - fontWeightOffset();
    if (showLeftScroll !== displayScroll.left || showRightScroll !== displayScroll.right) {
      setDisplayScroll({
        left: showLeftScroll,
        right: showRightScroll
      });
    }
  }, [displayScroll]);
  useEffect(function () {
    setSelectedTab(legacySelectedTab);
  }, [legacySelectedTab, setSelectedTab, tabItems]);
  useEffect(function () {
    window.addEventListener('resize', updateDisplayScrollState);
    return function () {
      return window.removeEventListener('resize', updateDisplayScrollState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty array ensures effect is only run on mount and unmount

  function handleScrollIntoView(tabRef, isSelected) {
    var tabMeta = tabRef.getBoundingClientRect();
    var tabListMeta = tabListRef.current.getBoundingClientRect();
    var offset = fontWeightOffset(isSelected);
    if (tabMeta.left - offset < tabListMeta.left) {
      // left side of item is out of view
      var nextScrollStart = tabListRef.current.scrollLeft - (tabListMeta.left - tabMeta.left) - offset;
      animate(tabListRef.current, nextScrollStart);
    } else if (tabMeta.right + offset > tabListMeta.right) {
      // right side of item is out of view
      var _nextScrollStart = tabListRef.current.scrollLeft + (tabMeta.right - tabListMeta.right) + offset;
      animate(tabListRef.current, _nextScrollStart);
    } else ;
  }
  function handleChange(event, i) {
    if (tabItems.length && !tabItems[i].disabled) {
      var isSelected = tabItems[i].value === selectedTab;
      setSelectedTab(tabItems[i].value);
      handleScrollIntoView(event.currentTarget, isSelected);
      handle(onChange)(event, tabItems[i].value);
    }
  }
  function handleScrollButtonClick(direction) {
    var delta = direction === SCROLL_DIRECTION.LEFT ? -tabListRef.current.clientWidth : tabListRef.current.clientWidth;
    var scrollValue = tabListRef.current.scrollLeft + delta;
    updateDisplayScrollState();
    animate(tabListRef.current, scrollValue);
  }
  function scrollButton(direction) {
    var ScrollIcon = direction === SCROLL_DIRECTION.LEFT ? ButtonLeftIcon : ButtonRightIcon;
    var scrollAriaLabel = direction === SCROLL_DIRECTION.LEFT ? scrollButtonAriaLabel.left : scrollButtonAriaLabel.right;
    return isScrollable && /*#__PURE__*/React.createElement(ScrollButton, {
      type: "button",
      onClick: function onClick() {
        return handleScrollButtonClick(direction);
      },
      theme: theme,
      disabled: !displayScroll[direction],
      "aria-label": scrollAriaLabel,
      "data-testid": direction === SCROLL_DIRECTION.LEFT ? 'left' : 'right'
    }, displayScroll[direction] && /*#__PURE__*/React.createElement(ScrollIcon, {
      fontSize: rem(SVG_ICON_SIZE_DEFAULT)
    }));
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TabListWrapper, null, scrollButton(SCROLL_DIRECTION.LEFT), /*#__PURE__*/React.createElement(TabList, {
    role: "tablist",
    tabAlignment: tabAlignment,
    mobileTabAlignment: mobileTabAlignment,
    "data-testid": "tablist",
    theme: theme,
    isSticky: isSticky,
    tabsWrapperStyle: tabsWrapperStyle,
    variant: variant,
    ref: tabListRef,
    showScrollButtons: showScrollButtons,
    onScroll: updateDisplayScrollState
  }, tabItems.map(function (elem, i) {
    return /*#__PURE__*/React.createElement(TabItem, {
      key: elem.id,
      role: "none",
      variant: variant
    }, /*#__PURE__*/React.createElement(TabButton, _extends({
      id: elem.id,
      type: "button",
      role: "tab",
      "data-text": elem.label,
      numTabs: numTabs,
      tabPosition: i,
      onClick: function onClick(e) {
        return handleChange(e, i);
      }
      // If there are no children, there won't be element to control
      ,
      "aria-controls": children ? "tabpanel-".concat(elem.id) : null,
      "aria-selected": elem.value === selectedTab,
      "aria-disabled": elem.disabled,
      selected: elem.value === selectedTab,
      disabled: elem.disabled,
      tabItemStyle: tabItemStyle,
      theme: theme,
      variant: variant
    }, safeProps), elem.label));
  })), scrollButton(SCROLL_DIRECTION.RIGHT)), renderSelectedTabContent(tabItems, selectedTab, children, customStyle));
};
process.env.NODE_ENV !== "production" ? Tabs.propTypes = {
  /** list of items to be displayed as tabs */
  tabItems: PropTypes.arrayOf(PropTypes.shape({
    /** id for the DOM element of the tab */
    id: PropTypes.string.isRequired,
    /** label to be displayed for the tab */
    label: PropTypes.string.isRequired,
    /** value used to determine selectedTab */
    value: PropTypes.string.isRequired,
    /** tab is not selectable if disabled */
    disabled: PropTypes.bool
  })),
  /** content for tabs passed using TabPanel component for each tab */
  children: componentWithName('TabPanel'),
  /**
   * currently selected tab (value)
   *
   * NOTE: Functionality for this prop will change in v4. It will make the component behave like a controlled component.
   * See [this issue](https://github.com/manulife-innersource/mux-core/issues/2386) for more information.
   * */
  selectedTab: PropTypes.string,
  /**
   * currently selected tab (value)
   *
   * NOTE: In v4, this prop will be renamed to `selectedTab`, replacing the functionality of the current `selectedTab` prop
   * */
  controlledSelectedTab: PropTypes.string,
  /** Default selected tab */
  defaultSelectedTab: PropTypes.string,
  /** stick tabs to the top on scroll (does not work in IE) */
  isSticky: PropTypes.bool,
  /** handler to be called when tab selection changes */
  onChange: PropTypes.func,
  /** tab alignment within the container for large screens */
  tabAlignment: PropTypes.oneOf([TAB_ALIGNMENT.LEFT, TAB_ALIGNMENT.RIGHT, TAB_ALIGNMENT.CENTER]),
  /** tab alignment within the container for mobile phone screens */
  mobileTabAlignment: PropTypes.oneOf([TAB_ALIGNMENT.LEFT, TAB_ALIGNMENT.RIGHT, TAB_ALIGNMENT.CENTER]),
  /** Style overrides */
  customStyle: PropTypes.shape({
    tabsWrapperStyle: PropTypes.object,
    tabItemStyle: PropTypes.object,
    tabPanelStyle: PropTypes.object
  }),
  /** Type of Tabs to use */
  variant: PropTypes.oneOf([TAB_VARIANT.DEFAULT, TAB_VARIANT.BOX, TAB_VARIANT.BOX_LINE_TOP, TAB_VARIANT.GREY]),
  /** aria-labels for the left and right scroll buttons for the GREY variant */
  scrollButtonAriaLabel: PropTypes.shape({
    left: PropTypes.string,
    right: PropTypes.string
  })
} : void 0;
Tabs.defaultProps = {
  tabItems: [],
  children: undefined,
  selectedTab: undefined,
  controlledSelectedTab: undefined,
  defaultSelectedTab: undefined,
  isSticky: false,
  onChange: noop,
  tabAlignment: TAB_ALIGNMENT.CENTER,
  mobileTabAlignment: TAB_ALIGNMENT.LEFT,
  customStyle: {
    tabsWrapperStyle: {},
    tabItemStyle: {},
    tabPanelStyle: {}
  },
  variant: TAB_VARIANT.DEFAULT,
  scrollButtonAriaLabel: {
    left: undefined,
    right: undefined
  }
};
process.env.NODE_ENV !== "production" ? Tabs.displayName = 'Tabs' : void 0;
var Tabs$1 = Tabs;

export { Tabs$1 as default };
